/**
 * Usage Service
 * Calculates and retrieves usage statistics for the current organization
 */

import { supabase } from '../lib/supabase'
import { SUBSCRIPTION_PLANS } from './subscriptionService'

/**
 * Get comprehensive usage statistics for an organization
 */
export async function getOrganizationUsage(organizationId) {
  try {
    // Get projects count
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('id, created_at')
      .eq('organization_id', organizationId)

    if (projectsError) throw projectsError

    // Get versions count (table is called project_versions)
    let versions = []
    const { data: versionData, error: versionsError } = await supabase
      .from('project_versions')
      .select('id, created_at, project_id, file_size')
      .in('project_id', projects?.map(p => p.id) || [])

    if (versionsError) {
      console.warn('Error fetching versions:', versionsError)
      // Continue without versions data
    } else {
      versions = versionData || []
    }

    // Calculate total storage in bytes from project_versions
    const totalStorageBytes = versions.reduce((sum, version) => sum + (version.file_size || 0), 0)
    const totalStorageGB = totalStorageBytes / (1024 * 1024 * 1024)

    // Get team members count
    const { data: members, error: membersError } = await supabase
      .from('organization_members')
      .select('id')
      .eq('organization_id', organizationId)

    if (membersError) throw membersError

    // Get current subscription
    let subscription = null
    try {
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      
      if (!subError) {
        subscription = subData
      }
    } catch (err) {
      // Subscription table might not exist yet, continue with free plan
      console.warn('Could not fetch subscription:', err)
    }
    
    // Determine limits based on plan
    let limits = {
      projects: 5,
      storage: 10, // GB
    }

    if (subscription?.plan_name === 'pro') {
      limits = {
        projects: 999999, // Unlimited
        storage: 100, // GB
      }
    }

    // Calculate usage percentages
    const projectsUsagePercent = subscription?.plan_name === 'pro' 
      ? 0 
      : Math.min((projects?.length || 0) / limits.projects * 100, 100)
    
    const storageUsagePercent = Math.min((totalStorageGB / limits.storage) * 100, 100)

    // Get activity for last 30 days
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    
    const recentProjects = projects?.filter(p => new Date(p.created_at) > thirtyDaysAgo).length || 0
    const recentVersions = versions.filter(v => new Date(v.created_at) > thirtyDaysAgo).length

    return {
      data: {
        // Current usage
        projects: {
          total: projects?.length || 0,
          limit: limits.projects,
          percentage: projectsUsagePercent,
          unlimited: subscription?.plan_name === 'pro'
        },
        versions: {
          total: versions.length
        },
        files: {
          total: versions.length  // In this schema, each version is one file
        },
        storage: {
          used: totalStorageGB,
          limit: limits.storage,
          percentage: storageUsagePercent,
          usedFormatted: formatBytes(totalStorageBytes),
          limitFormatted: `${limits.storage} GB`
        },
        team: {
          members: members?.length || 0
        },
        
        // Recent activity (last 30 days)
        recent: {
          projects: recentProjects,
          versions: recentVersions,
          files: recentVersions  // Same as versions in this schema
        },

        // Subscription info
        subscription: {
          plan: subscription?.plan_name || 'free',
          status: subscription?.status || 'inactive',
          expiresAt: subscription?.current_period_end
        }
      },
      error: null
    }
  } catch (error) {
    console.error('Error getting organization usage:', error)
    return { data: null, error }
  }
}

/**
 * Format bytes to human-readable format
 */
function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/**
 * Get usage trends over time (for charts)
 */
export async function getUsageTrends(organizationId, days = 30) {
  try {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    // Get projects created over time
    const { data: projects, error } = await supabase
      .from('projects')
      .select('created_at')
      .eq('organization_id', organizationId)
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: true })

    if (error) throw error

    return { data: projects, error: null }
  } catch (error) {
    console.error('Error getting usage trends:', error)
    return { data: null, error }
  }
}

/**
 * Check if organization has reached a limit
 */
export async function checkLimit(organizationId, limitType) {
  const { data: usage } = await getOrganizationUsage(organizationId)
  
  if (!usage) return { allowed: true, message: '' }

  switch (limitType) {
    case 'projects':
      if (usage.projects.unlimited) {
        return { allowed: true, message: '' }
      }
      if (usage.projects.total >= usage.projects.limit) {
        return {
          allowed: false,
          message: `You've reached your project limit (${usage.projects.limit}). Upgrade to Pro for unlimited projects.`
        }
      }
      return { allowed: true, message: '' }

    case 'storage':
      if (usage.storage.percentage >= 100) {
        return {
          allowed: false,
          message: `You've reached your storage limit (${usage.storage.limitFormatted}). Upgrade to increase storage.`
        }
      }
      if (usage.storage.percentage >= 90) {
        return {
          allowed: true,
          message: `Warning: You're using ${usage.storage.percentage.toFixed(0)}% of your storage.`
        }
      }
      return { allowed: true, message: '' }

    default:
      return { allowed: true, message: '' }
  }
}
